<template>
  <div
    class="rechercheViewWrapper"
    ref="rechercheViewWrapper"
  >
    <div class="searchWrapper searchWrapper--desktop">
      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        class="searchInput"
        size="large"
        state="default"
        placeholder="Référence virement, Facture…"
        deletable
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
      />
      <div class="searchWrapper--actions">
        <BasicTooltip
          label="Exporter les virements"
          position="left"
        >
          <BasicButton
            @click="exporter"
            size="large"
            icon-name="export"
            variant="outlined"
          />
        </BasicTooltip>
      </div>
    </div>
    <div class="searchWrapper searchWrapper--mobile">
      <div class="flex flex-gap-4">
        <BasicInput
          v-model="search"
          iconName="search"
          iconState="iconLeft"
          size="large"
          state="default"
          placeholder="Référence, Numéro Dossier..."
          style="width: 100%"
        />
        <BasicButton
          @click="exporter"
          size="large"
          icon-name="export"
          variant="outlined"
        />
      </div>
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
        width="full"
      />
    </div>
    <Transition>
      <div
        class="filtersWrapper"
        v-if="showFilters"
      >
        <WrapperDropdown
          v-model="periodeDateEmission"
          label="Période de paiement"
          :items="periodeDateEmissionItems"
          placeholder="Sélection"
          class="col-12 col-md-18 col-sm-36"
        />
        <div class="col-4 col-sm-36 actions--item">
          <BasicTooltip label="Ajouter les filtres aux favoris">
            <BasicButton
              :disabled="!searchIsFiltered"
              type="secondary"
              iconName="star"
              @click="addFavoris"
              style="display: inline"
            />
          </BasicTooltip>
          <BasicTooltip label="Supprimer les filtres">
            <BasicButton
              @click="removeFilters"
              :disabled="!searchIsFiltered"
              type="reverse"
              iconName="filter-remove"
              style="display: inline"
            />
          </BasicTooltip>
        </div>
      </div>
    </Transition>
    <div class="informationWrapper">
      <BasicPagination
        :nbPages="paginationNumberOfPages"
        :nbPagesMax="10"
        :currentPage="pageNumber"
        :nbResults="store.pageData?.totalRecords"
        @change="pageChange"
      />
      <BasicDropdown
        v-model="pageSize"
        :items="pageSizeItems"
        placeholder="Nombre de résultats"
        class="nbResult"
        force-value
      />
    </div>
    <div
      class="listComponentMainWrapper"
      ref="listComponentMainWrapper"
    >
      <div class="mobileWrapper">
        <VirementCardMobile
          v-for="(item, index) in store.pageData?.data"
          :key="index"
          :item
        />
      </div>
      <div class="desktopWrapper">
        <VirementHeader v-model="order" />
        <VirementCardDesktop
          v-for="(item, index) in store.pageData?.data"
          class="listCardDesktopWrapper"
          :key="index"
          :item
        />
      </div>
      <cards-layout
        v-if="!store.pageData?.data.length"
        cardsType="Virements"
        headerText="Virements"
        :hasHeader="false"
        :cards="[]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { onBeforeRouteUpdate } from 'vue-router'
  import { type VirementQueryDTO } from '@/features/api/extranet/dto/Virement/VirementQueryDTO'
  import { usePreferencesUtilisateur } from '@/features/application/preferences'
  import { PREFERENCES_UTILISATEUR_APP, type PreferenceGlobale } from '@/features/application/types'
  import { PeriodeLibelle } from '@/features/domaine/types/opco/shared/Periode'
  import { useImmerFieldPatcher } from '@/features/interface/composables'
  import { isObjectEquals } from '@/features/shared/tools'
  import { formatSearchQuery, parseSearchQuery } from '@/features/shared/tools/parse'
  import { useDialog } from '../../dialog'
  import { objectToDropdownItems } from '../../shared/tools'
  import { pageSizeItems } from '../dropdown-items'
  import type { PagedRequest } from '../storeRechercheBuilder'
  import { VirementCardDesktop, VirementCardMobile, VirementHeader } from './components'
  import { useRechercheVirementActions } from './useRechercheVirementActions'
  import { useRechercheVirementRoutingService } from './useRechercheVirementRoutingService'
  import { useRechercheVirementStore } from './useRechercheVirementStore'
  import CardsLayout from '@/features/interface/home/components/cardsLayout/CardsLayout.vue'

  const store = useRechercheVirementStore()
  const prefService = usePreferencesUtilisateur<PreferenceGlobale>(PREFERENCES_UTILISATEUR_APP)
  const showFilters = ref(false)
  const periodeDateEmissionItems = objectToDropdownItems(PeriodeLibelle)
  const { loadStoreFromLocationQuery, loadPageWithPagedRequest } = useRechercheVirementRoutingService()
  const { addFavoris } = useRechercheVirementActions()

  const { setupFieldWithKey, setupFieldWithOptions, setupCustomUpdater } = useImmerFieldPatcher<
    PagedRequest<VirementQueryDTO>
  >({
    getModel: () => store.pageRequest!,
    setModel(newModel) {
      const queryChanged = !isObjectEquals(store.pageRequest?.query, newModel.query)
      if (queryChanged) {
        newModel = {
          ...newModel,
          pagination: {
            pageNumber: 1,
            pageSize: newModel.pagination.pageSize,
          },
        }
      }
      loadPageWithPagedRequest(newModel)
    },
    debounceDelay: 1000,
  })

  const periodeDateEmission = setupFieldWithKey<string>('query.periodeDateEmission')
  const pageNumber = setupFieldWithKey<number>('pagination.pageNumber')

  const exporter = async () => {
    await store.printData()
    useDialog().showDialog({
      type: 'Ok',
      title: 'Export des virements',
      message: 'Les virements ont été exportés avec succés, vous allez les recevoir par mail',
    })
  }

  const pageSize = setupFieldWithOptions<number | null | undefined>({
    getValue: (model) => model.pagination?.pageSize,
    setPatch: (patch, value) => {
      patch.pagination = {
        pageNumber: 1,
        pageSize: value!,
      }
      prefService.setPreferencesAsync({ pageSize: value! })
    },
  })

  const search = setupFieldWithOptions<string>({
    getValue: () => formatSearchQuery(store.pageRequest?.query.search ?? []),
    setPatch: (patch, value) => {
      if (!value) patch.query.search = []
      else patch.query.search = parseSearchQuery(value)
    },
    withDebounce: true,
  })

  const order = setupFieldWithOptions<string>({
    getValue: () => {
      return store.pageRequest?.orderBy?.at(0) ?? ''
    },
    setPatch: (patch, value) => {
      patch.orderBy = value ? [value] : []
      patch.pagination = {
        pageNumber: 1,
        pageSize: patch.pagination.pageSize,
      }
    },
  })

  const paginationNumberOfPages = computed(() => {
    if (store?.pageData?.totalRecords) {
      return Math.ceil(store?.pageData?.totalRecords / store.pageRequest!.pagination!.pageSize!)
    }
    return 0
  })

  const searchIsFiltered = computed(() => search.value.length || periodeDateEmission.value.length)

  const removeFilters = setupCustomUpdater((pageRequest) => {
    pageRequest.query = {
      search: undefined,
      periodeDateEmission: undefined,
    }
    showFilters.value = false
  })

  onBeforeRouteUpdate(async (to) => {
    return await loadStoreFromLocationQuery(to.query)
  })

  const pageChange = (page: number) => {
    pageNumber.value = page
  }
</script>

<style scoped lang="less">
  @import '../index.less';
</style>
